import { Check, FileText } from 'lucide-react'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'

import { FormModal } from '@components/branded-modal'
import { AdasBox, AdasButton, AdasTypography } from '@components/wrapper-components'
import { INVOICE_EMAIL_VARIANT_CHOICE } from '../../constants'

// The three documents an Email Invoice send can carry. Retail is the plain document (custom
// discounts shown, client rebate left off, no watermark); cost adds the rebate, watermarked
// "Cost Invoice". "Both" sends the pair in a single email.
const OPTIONS = [
  {
    value: INVOICE_EMAIL_VARIANT_CHOICE.RETAIL,
    label: 'Retail Invoice',
    description: 'Custom discounts shown, client rebate left off. No watermark.'
  },
  {
    value: INVOICE_EMAIL_VARIANT_CHOICE.COST,
    label: 'Cost Invoice',
    description: 'Discounts and client rebate, with the fully discounted total. Watermarked “Cost Invoice”.'
  },
  {
    value: INVOICE_EMAIL_VARIANT_CHOICE.BOTH,
    label: 'Both Invoices',
    description: 'Send the retail and cost documents together in one email.'
  }
]

/** Step that runs ahead of the Email Invoice form: which invoice document(s) to send.
 *
 *  `onContinue` receives an INVOICE_EMAIL_VARIANT_CHOICE value. The caller expands it with
 *  `invoiceVariantsForEmailChoice`, fetches the matching PDFs and opens <EmailForm>. */
export const InvoiceEmailVariantModal = ({ open, onClose, onContinue, defaultChoice = INVOICE_EMAIL_VARIANT_CHOICE.RETAIL }) => {
  const [choice, setChoice] = useState(defaultChoice)

  // The modal stays mounted between opens, so reset to the default each time it reopens
  // rather than showing whatever was picked last time.
  useEffect(() => {
    if (open) setChoice(defaultChoice)
  }, [open, defaultChoice])

  const footer = (
    <div className='flex items-center justify-end gap-3'>
      <AdasButton variant='outlined' onClick={onClose} className='rounded-lg px-5'>
        CANCEL
      </AdasButton>
      <AdasButton
        variant='contained'
        onClick={() => onContinue(choice)}
        className='rounded-lg bg-[#0A1628] px-5 text-white hover:bg-[#1F2D3D]'
      >
        CONTINUE
      </AdasButton>
    </div>
  )

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title='Email Invoice'
      subtitle='Which invoice would you like to send?'
      icon={FileText}
      eyebrow='Select document'
      width='max-w-[480px]'
      footer={footer}
    >
      <AdasBox role='radiogroup' aria-label='Invoice document to send' className='flex flex-col gap-1.5'>
        {OPTIONS.map((option) => {
          const isChecked = choice === option.value
          return (
            <label
              key={option.value}
              className={`flex w-full cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                isChecked ? 'bg-[#E8EFFF]' : 'bg-white hover:bg-[#F8FAFC]'
              }`}
            >
              <span
                className={`mt-0.5 inline-flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border transition-colors ${
                  isChecked ? 'border-[#0A1628] bg-[#0A1628]' : 'border-[#D1D5D8] bg-white'
                }`}
              >
                {isChecked && <Check className='h-3 w-3 text-white' strokeWidth={3} />}
              </span>
              <input
                type='radio'
                name='invoice-email-variant'
                className='hidden'
                value={option.value}
                checked={isChecked}
                onChange={() => setChoice(option.value)}
              />
              <AdasBox className='min-w-0 flex-1'>
                <AdasTypography className='text-[14px] font-semibold text-[#0A1628]'>{option.label}</AdasTypography>
                <AdasTypography className='text-[13px] text-[#475569]'>{option.description}</AdasTypography>
              </AdasBox>
            </label>
          )
        })}
      </AdasBox>
    </FormModal>
  )
}

InvoiceEmailVariantModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  defaultChoice: PropTypes.oneOf(Object.values(INVOICE_EMAIL_VARIANT_CHOICE))
}
